/* eslint-disable react/no-unused-prop-types */
import * as React from 'react';
import { connect } from 'react-redux';
import { capitalize, snakeCase } from 'lodash';
import classnames from 'classnames';
import produce from 'immer';

import PromptExit from 'libs/router/prompt-exit/PromptExit';

import StepsProgressBar from 'components/progress/steps-progress-bar/StepsProgressBar';
import FieldDescriptionCard from 'components/inputs/field-description-card/FieldDescriptionCard';

import ProfileCreationStepIntro from 'modules/influencers/components/profile/ProfileCreationStepIntro';
import ProfileCreationSideIntro from 'modules/influencers/components/profile/ProfileCreationSideIntro';
import ProfileCreationStepGeneral from 'modules/influencers/components/profile/ProfileCreationStepGeneral';
import ProfileCreationGender from 'modules/influencers/components/profile/gender/ProfileCreationGender';
import ProfileCreationStepContentSpecialization from 'modules/influencers/components/profile/ProfileCreationStepAboutWork';
import ProfileCreationStepChannels from 'modules/influencers/components/profile/ProfileCreationStepChannels';
import ProfileAboutBio from 'modules/influencers/components/profile/bio/ProfileAboutBio';
import ProfileBoost from 'modules/influencers/components/profile/boost/ProfileBoost';
import ProfileCreationStepFinished from 'modules/influencers/components/profile/ProfileCreationStepFinished';
import ProfileCreationSideFinished from 'modules/influencers/components/profile/ProfileCreationSideFinished';

import { createInfluencerProfile, resetInfluencerProfile, saveInfluencerProfile } from 'modules/influencers/reducers/influencerProfile';
import { resetInfluencerImages } from 'modules/influencers/reducers/influencerImages';
import { logout } from 'modules/access/reducers/session';
import { danger } from 'redux-store/reducers/common-reducer';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { validateChannels } from 'modules/channels/reducers/channels';

import { __t, applyTranslations } from 'utils/language';
import { isLessThanDesktop } from 'utils/mobile-app';
import { redirectToIndexPage } from 'utils/routes';

applyTranslations(require('./influencer-create-profile.i18n.json'));

import type {
  Account,
  ChannelForm,
  ChannelsValidationResult,
  InfluencerProfile,
  InfluencerProfileForm
} from 'types';

import type { SyntheticEvent } from 'react';

type StateProps = {
  currentAccount: Account,
  profile: InfluencerProfile
};

type DispatchProps = {
  dispatchCreateInfluencerProfile: (details: InfluencerProfileForm) => Promise<any>,
  dispatchUpdateInfluencerProfile: (
    details: InfluencerProfileForm,
    profileId: number | null | undefined
  ) => Promise<any>,
  dispatchResetInfluencerProfile: () => Promise<any>,
  dispatchResetInfluencerImages: () => Promise<any>,
  dispatchLogout: () => Promise<any>,
  dispatchShowConfirmationModal: (
    a: {
      message: string,
      title: string,
      confirmButtonLabel: string,
      onConfirm: () => void,
      onCancel: () => void
    }
  ) => void,
  dispatchHideConfirmationModal: () => void,
  dispatchValidateChannels: (a: Array<ChannelForm>) => Promise<ChannelsValidationResult>,
  dispatchDanger: (message: string) => Promise<any>
};

type Props = StateProps & DispatchProps;

type State = {
  activeStep: number,
  details: InfluencerProfileForm,
  hasError: boolean,
  fieldDescription: {
    text: string,
    image: string | undefined | null,
    positionTop: number,
    width: number | null,
    component?: React.ReactNode
  } | undefined | null,
  skipPromptExit: boolean
};

export type FieldDescriptionShowParams = {
  name: string,
  positionTop: number,
  width: number | null,
  image?: string | null,
  component?: React.ReactNode
};

const STEPS = [
  __t('influencer_create_profile.steps.step_intro'),
  __t('influencer_create_profile.steps.step_main'),
  __t('influencer_create_profile.steps.step_gender'),
  __t('influencer_create_profile.steps.step_content_specialization'),
  __t('influencer_create_profile.steps.step_channels'),
  __t('influencer_create_profile.steps.step_bio'),
  __t('influencer_create_profile.steps.step_done'),
  __t('influencer_create_profile.steps.step_boost_intro'),
  __t('influencer_create_profile.steps.step_boost_data'),
  __t('influencer_create_profile.steps.step_boost_complete')
];

class InfluencerCreateProfile extends React.Component<Props, State> {
  state: State = {
    activeStep: 1,
    details: {
      isAgencyManaged: null,
      agencyDetails: null,
      artistName: '',
      channels: [],
      influencerFirstName: this.props.currentAccount && this.props.currentAccount.firstName || '',
      influencerLastName: this.props.currentAccount && this.props.currentAccount.lastName || '',
      influencerDateOfBirth: null,
      influencerGender: '',
      influencerGenderAdditional: null,
      influencerGenderSelfDescribed: null,
      locations: [],
      pitch: '',
      profileImage: null,
      ethnicGroup: null,
      profession: null,
      specializedIn: {},
      bodyAttributes: [],
      bodyTypeHeight: null,
      bodyTypeMass: null
    },
    hasError: false,
    fieldDescription: null,
    skipPromptExit: false
  };

  componentDidMount() {
    const {
      dispatchResetInfluencerProfile,
      dispatchResetInfluencerImages
    } = this.props;

    dispatchResetInfluencerProfile();
    dispatchResetInfluencerImages();
  }

  scrollToTheTop = () => {
    window.scrollTo(0, 0);
  };

  logout = () => {
    const { dispatchLogout } = this.props;

    dispatchLogout()
      .then(() => redirectToIndexPage());
  };

  handleLogout = () => {
    const { dispatchShowConfirmationModal, dispatchHideConfirmationModal } = this.props;

    dispatchShowConfirmationModal({
      title: __t('influencer_create_profile.confirm_exit.title'),
      message: __t('influencer_create_profile.confirm_exit.message'),
      confirmButtonLabel: __t('influencer_create_profile.confirm_exit.button_confirm'),
      onConfirm: () => {
        this.setState({
          skipPromptExit: true
        });
        dispatchHideConfirmationModal();
        this.logout();
      },
      onCancel: () => dispatchHideConfirmationModal()
    });
  };

  handlePrevStep = (details: InfluencerProfileForm) => {
    this.setState(state => ({
      activeStep: state.activeStep - 1,
      details: {
        ...state.details,
        ...details
      }
    }));

    this.scrollToTheTop();
  };

  handleNextStep = (details: Partial<InfluencerProfileForm> = {}) => {
    this.setState(state => ({
      activeStep: state.activeStep + 1,
      details: {
        ...state.details,
        ...details
      }
    }));

    this.scrollToTheTop();
  };

  handleInputFieldDescriptionShow = (event: SyntheticEvent<HTMLInputElement | HTMLOptionElement>) => {
    const target = event.currentTarget;

    if (typeof target.closest !== 'function') { // some IE does not support it
      return;
    }

    const parent = target.closest('.form-field');

    if (!parent) {
      return;
    }

    // @ts-ignore
    const targetName = target.name;
    // @ts-ignore
    const targetOffsetTop = parent.offsetTop;

    this.handleFieldDescriptionShow({
      name: targetName,
      positionTop: targetOffsetTop,
      width: null
    });
  };

  handleFieldDescriptionShow = ({
    name,
    positionTop,
    image,
    width,
    component
  }: FieldDescriptionShowParams): void => {
    this.setState({
      fieldDescription: {
        text: __t(`influencer_create_profile.field_descriptions.${snakeCase(name)}`),
        image,
        positionTop,
        width,
        component
      }
    });
  };

  handleFieldDescriptionHide = () => {
    this.setState({
      fieldDescription: null
    });
  };

  submit = (isUpdate: boolean = false) => {
    const { dispatchCreateInfluencerProfile, dispatchUpdateInfluencerProfile } = this.props;
    const { details } = this.state;

    const filteredDetails = produce(details, (draftState) => {
      // eslint-disable-next-line no-param-reassign
      draftState.channels = draftState.channels.filter(channel => channel.url);
    });

    const dispatchFunction = isUpdate
      ? dispatchUpdateInfluencerProfile
      : dispatchCreateInfluencerProfile;

    dispatchFunction(filteredDetails, details.id)
      .then((profile) => {
        this.setState({
          skipPromptExit: true
        });

        this.handleNextStep({ id: profile.id });
      })
      .catch(() => {
        window.scrollTo(0, 0);
      });
  };

  handleSubmit = () => {
    const { dispatchValidateChannels } = this.props;
    const { details } = this.state;

    this.setState({
      hasError: false
    });

    dispatchValidateChannels(details.channels)
      .then(result => this.setState({
        details: {
          ...details,
          ...result
        }
      }))
      .then(() => this.submit())
      .catch(result => {
        this.setState({
          details: {
            ...details,
            ...result
          }
        });
        const invalidChannels = result.channels.filter(channel => Boolean(channel.isInvalid));

        this.handleChannelValidationErrors(invalidChannels);
      });
  };

  handleSave = (details: InfluencerProfileForm) => {
    this.setState({
      details: {
        ...details
      }
    }, () => this.handleSubmit());
  };

  handleUpdate = (details: InfluencerProfileForm) => {
    this.setState({
      details: {
        ...details
      }
    }, () => this.submit(true));
  };

  handleChannelValidationErrors = (channels: Array<ChannelForm>) => {
    const { dispatchDanger } = this.props;
    const channelTypes = channels.map(channel => capitalize(channel.type)).join(', ');

    dispatchDanger(__t('influencer_create_profile.channels.validation_error', { channelTypes }));
  };

  handleFormValidationErrors = (): void => {
    this.setState({
      hasError: true
    });
  };

  renderLeftPart() {
    const { details, activeStep, hasError } = this.state;

    let component: React.ReactNode = null;

    switch (activeStep) {
      case 1:
        component = (
          <ProfileCreationStepIntro
            onSubmit={this.handleNextStep}
          />
        );
        break;
      case 2:
        component = (
          <ProfileCreationStepGeneral
            details={details}
            onSubmit={this.handleNextStep}
            onCancel={this.handlePrevStep}
            onInputFieldDescriptionShow={this.handleInputFieldDescriptionShow}
            onFieldDescriptionHide={this.handleFieldDescriptionHide}
          />
        );
        break;
      case 3:
        component = (
          <ProfileCreationGender
            details={details}
            onSubmit={this.handleNextStep}
            onCancel={this.handlePrevStep}
            onFieldDescriptionShow={this.handleFieldDescriptionShow}
            onFieldDescriptionHide={this.handleFieldDescriptionHide}
          />
        );
        break;
      case 4:
        component = (
          <ProfileCreationStepContentSpecialization
            details={details}
            onSubmit={this.handleNextStep}
            onCancel={this.handlePrevStep}
            onFieldDescriptionShow={this.handleFieldDescriptionShow}
            onFieldDescriptionHide={this.handleFieldDescriptionHide}
          />
        );
        break;
      case 5:
        component = (
          <ProfileCreationStepChannels
            key={`channels-have-errors-${String(details.channels.some(c => c.isInvalid))}`}
            details={details}
            hasError={hasError}
            onSubmit={this.handleNextStep}
            onCancel={this.handlePrevStep}
            onValidationErrors={this.handleFormValidationErrors}
            onFieldDescriptionShow={this.handleFieldDescriptionShow}
            onFieldDescriptionHide={this.handleFieldDescriptionHide}
          />
        );
        break;
      case 6:
        component = (
          <ProfileAboutBio
            details={details}
            onSubmit={this.handleSave}
            onCancel={this.handlePrevStep}
            onFieldDescriptionShow={this.handleFieldDescriptionShow}
            onFieldDescriptionHide={this.handleFieldDescriptionHide}
          />
        );
        break;
      case 7:
        component = (
          <ProfileCreationStepFinished
            details={details}
            onSubmit={this.handleNextStep}
          />
        );
        break;
      case 8:
      case 9:
      case 10:
        component = (
          <ProfileBoost
            activeStep={activeStep}
            details={details}
            onUpdate={this.handleUpdate}
            onSubmit={this.handleNextStep}
            onCancel={this.handlePrevStep}
            onFieldDescriptionShow={this.handleFieldDescriptionShow}
            onFieldDescriptionHide={this.handleFieldDescriptionHide}
          />
        );
        break;
      default:
        break;
    }

    return component;
  }

  renderRightPart() {
    const { activeStep } = this.state;

    let component: React.ReactNode = null;

    switch (activeStep) {
      case 1:
        component = (
          <ProfileCreationSideIntro />
        );
        break;
      case 7:
        component = (
          <ProfileCreationSideFinished />
        );
        break;
      default:
        break;
    }

    return component;
  }

  renderFieldDescriptionCard = (): React.ReactNode => {
    const { fieldDescription } = this.state;

    if (fieldDescription?.component) {
      return (
        <FieldDescriptionCard
          visible={Boolean(fieldDescription)}
          top={fieldDescription?.positionTop ?? 0}
          width={fieldDescription?.width ?? undefined}
        >
          {fieldDescription.component}
        </FieldDescriptionCard>
      );
    }

    return (
      <FieldDescriptionCard
        visible={Boolean(fieldDescription)}
        top={fieldDescription?.positionTop ?? 0}
        text={fieldDescription?.text ?? ''}
        width={fieldDescription?.width ?? undefined}
      >
        {
          fieldDescription && fieldDescription.image && (
            <img src={`/static/images/${fieldDescription.image}`} alt="" />
          )
        }
      </FieldDescriptionCard>
    );
  };

  render() {
    const { activeStep, skipPromptExit } = this.state;

    const partsClassess = classnames({
      'influencer-create-profile__parts': true,
      'influencer-create-profile__parts--block': activeStep === 6
    });

    const formClasses = classnames({
      'influencer-create-profile__form': true,
      'influencer-create-profile__form--plain': activeStep === 1
    });

    const rightPartClasses = classnames({
      'influencer-create-profile__part': true,
      'influencer-create-profile__part--right': true,
      'influencer-create-profile__part--primary': activeStep === 1,
      'influencer-create-profile__part--mobile': (activeStep === 7) && isLessThanDesktop()
    });

    return (
      <section
        className="influencer-create-profile"
        data-qa="influencer-create-profile"
      >
        {
          activeStep !== 1 && activeStep < STEPS.length && !skipPromptExit &&
            <PromptExit
              type="page"
            />
        }
        <StepsProgressBar
          steps={STEPS}
          activeStep={activeStep}
        >
          <div
            className="influencer-create-profile__logout"
            onClick={this.handleLogout}
          >
            {__t('influencer_create_profile.button_logout')}
          </div>
        </StepsProgressBar>
        <div className={partsClassess}>
          <div className="influencer-create-profile__part">
            <div className={formClasses}>
              {this.renderLeftPart()}
            </div>
          </div>
          <div className={rightPartClasses}>
            <div className="influencer-create-profile__field-description">
              {this.renderFieldDescriptionCard()}
            </div>
            {this.renderRightPart()}
          </div>
        </div>
      </section>
    );
  }
}

const mapStateToProps = ({ account, influencerProfile }): StateProps => ({
  currentAccount: account.currentAccount,
  profile: influencerProfile.profile
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchCreateInfluencerProfile: (details) => dispatch(createInfluencerProfile(details)),
  dispatchUpdateInfluencerProfile: (details, profileId) => {
    if (profileId) {
      return dispatch(saveInfluencerProfile(profileId, details));
    }

    return null;
  },
  dispatchResetInfluencerProfile: () => dispatch(resetInfluencerProfile()),
  dispatchResetInfluencerImages: () => dispatch(resetInfluencerImages()),
  dispatchLogout: () => dispatch(logout()),
  dispatchShowConfirmationModal: (params) => dispatch(showConfirmModal(params)),
  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal()),
  dispatchValidateChannels: (channels) => dispatch(validateChannels(channels)),
  dispatchDanger: message => dispatch(danger(message))
});

export default connect(mapStateToProps, mapDispatchToProps)(InfluencerCreateProfile);
